/**
 * Value Type Registry
 * Central registry of all available column types for summary spreadsheets
 */

import { DEFAULT_DISPLAY_FORMATS } from './displayFormats';
import { ValueType, ValueTypeCategory } from './valueType';

// ============================================
// Column Type IDs (Constants)
// ============================================

export const COLUMN_TYPES = {
  // Identifiers
  COMPANY_NAME: 'company_name',
  FUND_NAME: 'fund_name',

  // Dates
  VALUATION_DATE: 'valuation_date',
  INITIAL_INVESTMENT_DATE: 'initial_investment_date',
  MOST_RECENT_INVESTMENT_DATE: 'most_recent_investment_date',

  // Ownership
  DILUTED_OWNERSHIP: 'diluted_ownership',
  OUTSTANDING_OWNERSHIP: 'outstanding_ownership',

  // Values
  INVESTED_CAPITAL: 'invested_capital',
  REALIZED_VALUE: 'realized_value',
  UNREALIZED_VALUE: 'unrealized_value',
  TOTAL_VALUE: 'total_value',
  BREAKEVEN_EXIT_EQUITY_VALUE: 'breakeven_exit_equity_value',
  EQUITY_VALUE: 'equity_value',
  ENTERPRISE_VALUE: 'enterprise_value',
  CURRENT_FUND_VALUE: 'current_fund_value',
  TOTAL_INSIGHT_PREFERENCE: 'total_insight_preference',
  FUND_PREFERENCE: 'fund_preference',
  SPECIFIC_PREFERENCE: 'specific_preference',
  TOTAL_COMPANY_PREFERENCE: 'total_company_preference',

  // Metrics
  GROSS_IRR: 'gross_irr',
  MOIC: 'moic',
  PERCENT_CHANGE_FROM_PREVIOUS: 'percent_change_from_previous',
  IMPLIED_ARR_MULTIPLE: 'implied_arr_multiple',
  DCF_DISCOUNT_RATE: 'dcf_discount_rate',
  PROJECTED_REVENUE_YEAR_1: 'projected_revenue_year_1',
  PROJECTED_REVENUE_YEAR_2: 'projected_revenue_year_2',
  PROJECTED_REVENUE_YEAR_3: 'projected_revenue_year_3',

  // Valuation Attributes
  ALLOCATION_METHOD: 'allocation_method',
  ENTERPRISE_VALUATION_METHOD: 'enterprise_valuation_method',
  VALUATION_APPROACH: 'valuation_approach',
  VALUATION_STATUS: 'valuation_status',

  // Process
  PROCESS_MANAGEMENT: 'process_management',

  // Company General
  CAP_TABLE_CURRENCY: 'cap_table_currency',
  FINANCIALS_CURRENCY: 'financials_currency',
  FISCAL_YEAR_END: 'fiscal_year_end',
  WEBSITE: 'website',
  LEGAL_COMPANY_NAME: 'legal_company_name',

  // Cap Table
  CAP_TABLE_VERSION_NAME: 'cap_table_version_name',
  TOTAL_OUTSTANDING_SHARES: 'total_outstanding_shares',
  TOTAL_FULLY_DILUTED_SHARES: 'total_fully_diluted_shares',
  TOTAL_PREFERRED_ROUNDS: 'total_preferred_rounds',
  TOTAL_OPTIONS: 'total_options',
  AVG_OPTION_STRIKE_PRICE: 'avg_option_strike_price',
  OPTION_POOL_PERCENT: 'option_pool_percent',
  TOTAL_WARRANTS: 'total_warrants',
  AVG_WARRANT_STRIKE_PRICE: 'avg_warrant_strike_price',
  TOTAL_UNISSUED_OPTIONS: 'total_unissued_options',
  TOTAL_COMMON_SHARES: 'total_common_shares',
  TOTAL_OUTSTANDING_PREFERRED_SHARES: 'total_outstanding_preferred_shares',
  TOTAL_FULLY_DILUTED_PREFERRED_SHARES: 'total_fully_diluted_preferred_shares',
  TOTAL_RAISED: 'total_raised',
  TOTAL_INITIAL_LIQUIDATION_PREFERENCE: 'total_initial_liquidation_preference',
  TOTAL_PREFERENCE_WITH_DIVIDENDS: 'total_preference_with_dividends',
  INITIAL_NOTE_ISSUANCE_DATE: 'initial_note_issuance_date',
  INITIAL_NOTE_CONVERSION_MODEL: 'initial_note_conversion_model',
  INITIAL_NOTE_CONVERSION_PRICE: 'initial_note_conversion_price',
  INITIAL_NOTE_EXPECTED_SHARES: 'initial_note_expected_shares',
  TOTAL_NOTE_EXPECTED_SHARES: 'total_note_expected_shares',

  // Fund Ownership
  SECURITY_ID: 'security_id',
  CASH_DISTRIBUTIONS: 'cash_distributions',
  PROCEEDS_FROM_SHARES_SOLD: 'proceeds_from_shares_sold',
  LOAN_VALUE: 'loan_value',

  // Breakpoint Analysis
  USES_CUSTOM_BREAKPOINTS: 'uses_custom_breakpoints',
  HIGHEST_BREAKPOINT_PRICE: 'highest_breakpoint_price',
  HIGHEST_BREAKPOINT_LIMIT: 'highest_breakpoint_limit',

  // Cash Flows
  NET_CASH_FLOWS: 'net_cash_flows',
  TOTAL_OTHER_CAPITAL_COSTS: 'total_other_capital_costs',

  // Financials - Dates
  FINANCIALS_DATE: 'financials_date',
  LTM_DATE: 'ltm_date',
  NTM_DATE: 'ntm_date',
  BALANCE_SHEET_DATE: 'balance_sheet_date',

  // Financials - Income Statement (LTM/NTM)
  TOTAL_REVENUE: 'total_revenue',
  TOTAL_COST_OF_SALES: 'total_cost_of_sales',
  GROSS_PROFIT: 'gross_profit',
  OPERATING_EXPENSES: 'operating_expenses',
  EBITDA: 'ebitda',
  ADJUSTED_EBITDA: 'adjusted_ebitda',
  DEPRECIATION_EXPENSE: 'depreciation_expense',
  AMORTIZATION_EXPENSE: 'amortization_expense',
  EBIT: 'ebit',
  INTEREST_EXPENSE: 'interest_expense',
  OTHER_EXPENSE: 'other_expense',
  PRETAX_INCOME: 'pretax_income',
  INCOME_TAXES: 'income_taxes',
  NET_INCOME: 'net_income',

  // Financials - Margins
  REVENUE_GROWTH_RATE: 'revenue_growth_rate',
  COST_OF_SALES_PERCENT: 'cost_of_sales_percent',
  GROSS_MARGIN: 'gross_margin',
  OPERATING_PERCENT_OF_SALES: 'operating_percent_of_sales',
  EBITDA_MARGIN: 'ebitda_margin',
  ADJUSTED_EBITDA_MARGIN: 'adjusted_ebitda_margin',
  NET_PROFIT_MARGIN: 'net_profit_margin',

  // Balance Sheet
  CASH_AND_EQUIVALENTS: 'cash_and_equivalents',
  ACCOUNTS_RECEIVABLES: 'accounts_receivables',
  INVENTORY: 'inventory',
  OTHER_CURRENT_ASSETS: 'other_current_assets',
  TOTAL_CURRENT_ASSETS: 'total_current_assets',
  PPE: 'ppe',
  INTANGIBLES: 'intangibles',
  OTHER_LONG_TERM_ASSETS: 'other_long_term_assets',
  TOTAL_LONG_TERM_ASSETS: 'total_long_term_assets',
  TOTAL_ASSETS: 'total_assets',
  SHORT_TERM_DEBT: 'short_term_debt',
  ACCOUNTS_PAYABLE: 'accounts_payable',
  ACCRUED_LIABILITIES: 'accrued_liabilities',
  DEFERRED_REVENUE: 'deferred_revenue',
  OTHER_CURRENT_LIABILITIES: 'other_current_liabilities',
  TOTAL_CURRENT_LIABILITIES: 'total_current_liabilities',
  LONG_TERM_DEBT: 'long_term_debt',
  OTHER_LONG_TERM_DEBT: 'other_long_term_debt',
  TOTAL_LONG_TERM_DEBT: 'total_long_term_debt',
  TOTAL_LIABILITIES: 'total_liabilities',
  BS_EQUITY: 'bs_equity',
  TOTAL_LIABILITIES_AND_EQUITY: 'total_liabilities_and_equity',

  // Implied Multiples
  IMPLIED_EV_LTM_REVENUE: 'implied_ev_ltm_revenue',
  IMPLIED_EV_LTM_EBITDA: 'implied_ev_ltm_ebitda',
  IMPLIED_EV_NTM_REVENUE: 'implied_ev_ntm_revenue',
  IMPLIED_EV_NTM_EBITDA: 'implied_ev_ntm_ebitda',
  IMPLIED_EV_KPI: 'implied_ev_kpi',

  // GPC
  GPC_USE_MULTIPLE_PREMIUM_DISCOUNT: 'gpc_use_multiple_premium_discount',
  GPC_USE_EQUITY_VALUE: 'gpc_use_equity_value',
  GPC_USE_CALCULATED_FORWARD_MULTIPLE: 'gpc_use_calculated_forward_multiple',
  GPC_FIRST_MULTIPLE_TYPE: 'gpc_first_multiple_type',
  GPC_SECOND_MULTIPLE_TYPE: 'gpc_second_multiple_type',
  GPC_LTM_REVENUE_APPLIED_MULTIPLE: 'gpc_ltm_revenue_applied_multiple',
  GPC_LTM_EBITDA_APPLIED_MULTIPLE: 'gpc_ltm_ebitda_applied_multiple',
  GPC_NTM_REVENUE_APPLIED_MULTIPLE: 'gpc_ntm_revenue_applied_multiple',
  GPC_NTM_EBITDA_APPLIED_MULTIPLE: 'gpc_ntm_ebitda_applied_multiple',
  GPC_WEIGHTED_APPLIED_MULTIPLE: 'gpc_weighted_applied_multiple',
  GPC_LTM_REVENUE_PREMIUM_DISCOUNT: 'gpc_ltm_revenue_premium_discount',
  GPC_LTM_EBITDA_PREMIUM_DISCOUNT: 'gpc_ltm_ebitda_premium_discount',
  GPC_NTM_REVENUE_PREMIUM_DISCOUNT: 'gpc_ntm_revenue_premium_discount',
  GPC_NTM_EBITDA_PREMIUM_DISCOUNT: 'gpc_ntm_ebitda_premium_discount',
  GPC_WEIGHTED_PREMIUM_DISCOUNT: 'gpc_weighted_premium_discount',
  GPC_WEIGHTED_ENTERPRISE_VALUE: 'gpc_weighted_enterprise_value',
  GPC_WEIGHTED_EQUITY_VALUE: 'gpc_weighted_equity_value',
  GPC_ALL_TICKERS: 'gpc_all_tickers',
  GPC_PERCENTILE_LTM_REVENUE: 'gpc_percentile_ltm_revenue',
  GPC_PERCENTILE_LTM_REVENUE_GROWTH: 'gpc_percentile_ltm_revenue_growth',
  GPC_PERCENTILE_NTM_REVENUE: 'gpc_percentile_ntm_revenue',
  GPC_PERCENTILE_NTM_REVENUE_GROWTH: 'gpc_percentile_ntm_revenue_growth',
  GPC_PERCENTILE_LTM_EBITDA: 'gpc_percentile_ltm_ebitda',
  GPC_PERCENTILE_NTM_EBITDA: 'gpc_percentile_ntm_ebitda',
  GPC_PERCENTILE_GROSS_MARGIN: 'gpc_percentile_gross_margin',
  GPC_PERCENTILE_EBITDA_MARGIN: 'gpc_percentile_ebitda_margin',
  GPC_PERCENTILE_TANGIBLE_BOOK_VALUE: 'gpc_percentile_tangible_book_value',
  GPC_PERCENTILE_NET_INCOME: 'gpc_percentile_net_income',
  GPC_SELECTED_1Y_VOLATILITY: 'gpc_selected_1y_volatility',
  GPC_1Y_VOLATILITY: 'gpc_1y_volatility',
  GPC_SELECTED_2Y_VOLATILITY: 'gpc_selected_2y_volatility',
  GPC_2Y_VOLATILITY: 'gpc_2y_volatility',
  GPC_SELECTED_5Y_VOLATILITY: 'gpc_selected_5y_volatility',
  GPC_5Y_VOLATILITY: 'gpc_5y_volatility',

  // Transaction Comps
  TC_LTM_REVENUE_APPLIED_MULTIPLE: 'tc_ltm_revenue_applied_multiple',
  TC_LTM_EBITDA_APPLIED_MULTIPLE: 'tc_ltm_ebitda_applied_multiple',
  TC_NTM_REVENUE_APPLIED_MULTIPLE: 'tc_ntm_revenue_applied_multiple',
  TC_NTM_EBITDA_APPLIED_MULTIPLE: 'tc_ntm_ebitda_applied_multiple',
  TC_WEIGHTED_APPLIED_MULTIPLE: 'tc_weighted_applied_multiple',
  TC_WEIGHTED_ENTERPRISE_VALUE: 'tc_weighted_enterprise_value',
  TC_WEIGHTED_EQUITY_VALUE: 'tc_weighted_equity_value',
  TC_PERCENTILE_LTM_REVENUE: 'tc_percentile_ltm_revenue',
  TC_PERCENTILE_LTM_REVENUE_GROWTH: 'tc_percentile_ltm_revenue_growth',
  TC_PERCENTILE_NTM_REVENUE: 'tc_percentile_ntm_revenue',
  TC_PERCENTILE_NTM_REVENUE_GROWTH: 'tc_percentile_ntm_revenue_growth',
  TC_PERCENTILE_LTM_EBITDA: 'tc_percentile_ltm_ebitda',
  TC_PERCENTILE_NTM_EBITDA: 'tc_percentile_ntm_ebitda',
  TC_PERCENTILE_GROSS_MARGIN: 'tc_percentile_gross_margin',
  TC_PERCENTILE_EBITDA_MARGIN: 'tc_percentile_ebitda_margin',

  // Backsolve
  BACKSOLVE_ALLOCATION_METHODS: 'backsolve_allocation_methods',
  BACKSOLVE_SECURITY_NAMES: 'backsolve_security_names',
  BACKSOLVE_TARGET_TOTAL_SHARES: 'backsolve_target_total_shares',
  BACKSOLVE_TARGET_TOTAL_VALUE: 'backsolve_target_total_value',
  BACKSOLVE_OPM_DATE: 'backsolve_opm_date',
  BACKSOLVE_OPM_VOLATILITY: 'backsolve_opm_volatility',
  BACKSOLVE_OPM_MATURITY: 'backsolve_opm_maturity',
  BACKSOLVE_OPM_MATURITY_DATE: 'backsolve_opm_maturity_date',
  BACKSOLVE_OPM_RISK_FREE_RATE: 'backsolve_opm_risk_free_rate',
  BACKSOLVE_IMPLIED_EQUITY_VALUE: 'backsolve_implied_equity_value',
  BACKSOLVE_ENTERPRISE_VALUE: 'backsolve_enterprise_value',
  BACKSOLVE_USES_MARKET_ADJUSTMENT: 'backsolve_uses_market_adjustment',
  BACKSOLVE_MARKET_ADJUSTMENT_DATE: 'backsolve_market_adjustment_date',
  BACKSOLVE_METRIC_BASIS: 'backsolve_metric_basis',
  BACKSOLVE_MARKET_ADJUSTMENT: 'backsolve_market_adjustment',
  BACKSOLVE_ADJUSTED_VALUE: 'backsolve_adjusted_value',
  BACKSOLVE_ADJUSTED_ENTERPRISE_VALUE: 'backsolve_adjusted_enterprise_value',
  BACKSOLVE_ADJUSTED_EQUITY_VALUE: 'backsolve_adjusted_equity_value',

  // External Value
  EXTERNAL_EQUITY_VALUE: 'external_equity_value',
  EXTERNAL_ENTERPRISE_VALUE: 'external_enterprise_value',

  // DCF (detailed)
  DCF_WACC: 'dcf_wacc',
  DCF_EFFECTIVE_TAX_RATE: 'dcf_effective_tax_rate',
  DCF_PV_DISCRETE_CASH_FLOWS: 'dcf_pv_discrete_cash_flows',
  DCF_PV_TERMINAL_VALUE: 'dcf_pv_terminal_value',
  DCF_PV_NOL_CARRYFORWARDS: 'dcf_pv_nol_carryforwards',
  DCF_ENTERPRISE_VALUE: 'dcf_enterprise_value',

  // Specified Share Value
  SSV_EQUITY_VALUE: 'ssv_equity_value',
  SSV_ENTERPRISE_VALUE: 'ssv_enterprise_value',

  // Calibration
  CAL_LTM_REVENUE_MULTIPLE: 'cal_ltm_revenue_multiple',
  CAL_NTM_REVENUE_MULTIPLE: 'cal_ntm_revenue_multiple',
  CAL_LTM_REVENUE_PERCENTILE: 'cal_ltm_revenue_percentile',
  CAL_NTM_REVENUE_PERCENTILE: 'cal_ntm_revenue_percentile',
  CAL_REVENUE_MULTIPLE_TYPE: 'cal_revenue_multiple_type',
  CAL_LTM_REVENUE_PREMIUM: 'cal_ltm_revenue_premium',
  CAL_NTM_REVENUE_PREMIUM: 'cal_ntm_revenue_premium',
  CAL_LTM_EBITDA_MULTIPLE: 'cal_ltm_ebitda_multiple',
  CAL_NTM_EBITDA_MULTIPLE: 'cal_ntm_ebitda_multiple',
  CAL_LTM_EBITDA_PERCENTILE: 'cal_ltm_ebitda_percentile',
  CAL_NTM_EBITDA_PERCENTILE: 'cal_ntm_ebitda_percentile',
  CAL_EBITDA_MULTIPLE_TYPE: 'cal_ebitda_multiple_type',
  CAL_LTM_EBITDA_PREMIUM: 'cal_ltm_ebitda_premium',
  CAL_NTM_EBITDA_PREMIUM: 'cal_ntm_ebitda_premium',

  // Documents & Process Management
  TOTAL_DOCUMENTS: 'total_documents',
  REFERENCED_DOCUMENTS: 'referenced_documents',
  PENDING_DOCUMENT_REQUESTS: 'pending_document_requests',
  PENDING_QUESTIONS: 'pending_questions',

  // Process Management (optional)
  PROCESS_STATUS: 'process_status',
} as const;

export type ColumnTypeId = (typeof COLUMN_TYPES)[keyof typeof COLUMN_TYPES];

// ============================================
// Value Type Definitions
// ============================================

/**
 * All available value types for summary spreadsheets
 */
export const VALUE_TYPE_DEFINITIONS: Record<string, ValueType> = {
  // ============================================
  // Identifier Columns
  // ============================================
  [COLUMN_TYPES.COMPANY_NAME]: {
    id: COLUMN_TYPES.COMPANY_NAME,
    displayName: 'Company',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'company_name' },
    metadata: {
      description: 'Name of the portfolio company',
      category: 'identifier',
      isRequired: true,
      isLocked: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.FUND_NAME]: {
    id: COLUMN_TYPES.FUND_NAME,
    displayName: 'Fund',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'fund_name' },
    metadata: {
      description: 'Name of the fund',
      category: 'identifier',
      isRequired: false,
      summaryType: 'firm', // Only shown in firm summary
    },
  },

  // ============================================
  // Date Columns
  // ============================================
  [COLUMN_TYPES.VALUATION_DATE]: {
    id: COLUMN_TYPES.VALUATION_DATE,
    displayName: 'Valuation Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'measurement_date' },
    metadata: {
      description: 'Most recent valuation/measurement date',
      category: 'dates',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.INITIAL_INVESTMENT_DATE]: {
    id: COLUMN_TYPES.INITIAL_INVESTMENT_DATE,
    displayName: 'Initial Investment',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'initial_investment_date' },
    metadata: {
      description: 'Date of initial investment',
      category: 'dates',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE]: {
    id: COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE,
    displayName: 'Most Recent Investment',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'most_recent_investment_date' },
    metadata: {
      description: 'Date of most recent investment',
      category: 'dates',
      summaryType: 'both',
    },
  },

  // ============================================
  // Ownership Columns
  // ============================================
  [COLUMN_TYPES.DILUTED_OWNERSHIP]: {
    id: COLUMN_TYPES.DILUTED_OWNERSHIP,
    displayName: 'Fully Diluted Ownership %',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'diluted_ownership' },
    metadata: {
      description: 'Fully diluted ownership percentage',
      category: 'ownership',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.OUTSTANDING_OWNERSHIP]: {
    id: COLUMN_TYPES.OUTSTANDING_OWNERSHIP,
    displayName: 'Outstanding Ownership %',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'outstanding_ownership' },
    metadata: {
      description: 'Outstanding ownership percentage',
      category: 'ownership',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Value Columns
  // ============================================
  [COLUMN_TYPES.INVESTED_CAPITAL]: {
    id: COLUMN_TYPES.INVESTED_CAPITAL,
    displayName: 'Invested Capital',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'fund_cost_basis' },
    metadata: {
      description: 'Total capital invested (cost basis)',
      category: 'values',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.REALIZED_VALUE]: {
    id: COLUMN_TYPES.REALIZED_VALUE,
    displayName: 'Realized Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'realized_value' },
    metadata: {
      description: 'Value that has been realized (distributions, exits)',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.UNREALIZED_VALUE]: {
    id: COLUMN_TYPES.UNREALIZED_VALUE,
    displayName: 'Unrealized Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'unrealized_value' },
    metadata: {
      description: 'Current unrealized value of the investment',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_VALUE]: {
    id: COLUMN_TYPES.TOTAL_VALUE,
    displayName: 'Total Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.REALIZED_VALUE, COLUMN_TYPES.UNREALIZED_VALUE],
    calculation: {
      strategy: 'excel-formula',
      template: '={realized_value}+{unrealized_value}',
    },
    metadata: {
      description: 'Sum of realized and unrealized values',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE]: {
    id: COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE,
    displayName: 'Breakeven Exit Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'breakeven_exit_equity_value' },
    metadata: {
      description: 'Exit equity value needed to achieve the breakeven multiple (may be a range)',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.EQUITY_VALUE]: {
    id: COLUMN_TYPES.EQUITY_VALUE,
    displayName: 'Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'equity_value' },
    metadata: {
      description: 'Equity value from the current valuation',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.ENTERPRISE_VALUE,
    displayName: 'Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'enterprise_value' },
    metadata: {
      description: 'Enterprise value from the current valuation',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.CURRENT_FUND_VALUE]: {
    id: COLUMN_TYPES.CURRENT_FUND_VALUE,
    displayName: 'Current Fund Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'current_fund_value' },
    metadata: {
      description: 'Current fund value (unrealized value)',
      category: 'values',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE]: {
    id: COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE,
    displayName: 'Total Insight Preference',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'total_insight_preference' },
    metadata: {
      description: 'Total preference for the firm across funds',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.FUND_PREFERENCE]: {
    id: COLUMN_TYPES.FUND_PREFERENCE,
    displayName: 'Fund Preference',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'fund_preference' },
    metadata: {
      description: 'Preference value for the fund',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.SPECIFIC_PREFERENCE]: {
    id: COLUMN_TYPES.SPECIFIC_PREFERENCE,
    displayName: 'Specific Preference',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'specific_preference' },
    metadata: {
      description: 'Specific/inside preference value',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE]: {
    id: COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE,
    displayName: 'Total Company Preference',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'total_company_preference' },
    metadata: {
      description: 'Total preference value for the company',
      category: 'values',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Metric Columns
  // ============================================
  [COLUMN_TYPES.GROSS_IRR]: {
    id: COLUMN_TYPES.GROSS_IRR,
    displayName: 'Gross IRR',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple', // Calculated on backend due to complexity
    dependsOn: [],
    dataSource: { type: 'field', path: 'gross_irr' },
    metadata: {
      description: 'Gross Internal Rate of Return',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.MOIC]: {
    id: COLUMN_TYPES.MOIC,
    displayName: 'MOIC',
    displayFormat: DEFAULT_DISPLAY_FORMATS.moic,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.TOTAL_VALUE, COLUMN_TYPES.INVESTED_CAPITAL],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({invested_capital}=0, 0, {total_value}/{invested_capital})',
    },
    metadata: {
      description: 'Multiple on Invested Capital (Total Value / Invested Capital)',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS]: {
    id: COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS,
    displayName: '% Change from Previous',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'percent_change_from_previous' },
    metadata: {
      description: 'Percent change from previous measurement date',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.IMPLIED_ARR_MULTIPLE]: {
    id: COLUMN_TYPES.IMPLIED_ARR_MULTIPLE,
    displayName: 'Implied ARR Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.moic,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'implied_arr_multiple' },
    metadata: {
      description: 'Implied ARR multiple based on valuation and financials',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DCF_DISCOUNT_RATE]: {
    id: COLUMN_TYPES.DCF_DISCOUNT_RATE,
    displayName: 'DCF Discount Rate',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'dcf_discount_rate' },
    metadata: {
      description: 'Discount rate used in DCF (WACC)',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1]: {
    id: COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1,
    displayName: 'Projected Revenue (Year +1)',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'projected_revenue_year_1' },
    metadata: {
      description: 'Projected revenue for the first forecast year',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2]: {
    id: COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2,
    displayName: 'Projected Revenue (Year +2)',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'projected_revenue_year_2' },
    metadata: {
      description: 'Projected revenue for the second forecast year',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3]: {
    id: COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3,
    displayName: 'Projected Revenue (Year +3)',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'projected_revenue_year_3' },
    metadata: {
      description: 'Projected revenue for the third forecast year',
      category: 'values',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.ALLOCATION_METHOD]: {
    id: COLUMN_TYPES.ALLOCATION_METHOD,
    displayName: 'Allocation Method',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'allocation_method' },
    metadata: {
      description: 'Allocation method used (CSE, OPM, etc)',
      category: 'custom',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD]: {
    id: COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD,
    displayName: 'Valuation Approaches',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'enterprise_valuation_method' },
    metadata: {
      description: 'Valuation method(s) used to derive enterprise value',
      category: 'custom',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.VALUATION_APPROACH]: {
    id: COLUMN_TYPES.VALUATION_APPROACH,
    displayName: 'Valuation Approach',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'valuation_approach' },
    metadata: {
      description: 'Valuation approach used (DCF, Market Approach, etc.)',
      category: 'custom',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.VALUATION_STATUS]: {
    id: COLUMN_TYPES.VALUATION_STATUS,
    displayName: 'Valuation Status',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'valuation_status' },
    metadata: {
      description: 'Valuation status (Draft, Published, Final)',
      category: 'custom',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PROCESS_MANAGEMENT]: {
    id: COLUMN_TYPES.PROCESS_MANAGEMENT,
    displayName: 'Process Management',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    // Rendered entirely client-side (TaskCell); the data endpoint does not resolve a value for it.
    metadata: {
      description: 'Process management tasks for the valuation',
      category: 'process',
      summaryType: 'firm',
      // No backend row value, so it must be kept out of the sort/filter choices.
      isClientOnly: true,
    },
  },

  //
  // TODO: Find any dependencies for these columns
  // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

  // ============================================
  // Company General Fields
  // ============================================
  [COLUMN_TYPES.CAP_TABLE_CURRENCY]: {
    id: COLUMN_TYPES.CAP_TABLE_CURRENCY,
    displayName: 'Cap Table Currency',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'company_captable_currency' },
    metadata: {
      description: 'Currency used for the cap table',
      category: 'identifier',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.FINANCIALS_CURRENCY]: {
    id: COLUMN_TYPES.FINANCIALS_CURRENCY,
    displayName: 'Financials Currency',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'financialsCurrencyResolver' },
    metadata: {
      description: 'Currency used for financial statements',
      category: 'identifier',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.FISCAL_YEAR_END]: {
    id: COLUMN_TYPES.FISCAL_YEAR_END,
    displayName: 'Fiscal Year End',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'fiscalYearEndResolver' },
    metadata: {
      description: 'Company fiscal year end month',
      category: 'identifier',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.WEBSITE]: {
    id: COLUMN_TYPES.WEBSITE,
    displayName: 'Website',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'websiteResolver' },
    metadata: {
      description: 'Company website URL',
      category: 'identifier',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.LEGAL_COMPANY_NAME]: {
    id: COLUMN_TYPES.LEGAL_COMPANY_NAME,
    displayName: 'Legal Company Name',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'legalCompanyNameResolver' },
    metadata: {
      description: 'Legal registered name of the company',
      category: 'identifier',
      summaryType: 'both',
    },
  },

  // ============================================
  // Cap Table Columns
  // ============================================
  [COLUMN_TYPES.CAP_TABLE_VERSION_NAME]: {
    id: COLUMN_TYPES.CAP_TABLE_VERSION_NAME,
    displayName: 'Cap Table Version',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'capTableVersionNameResolver' },
    metadata: {
      description: 'Name of the cap table version used',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_OUTSTANDING_SHARES]: {
    id: COLUMN_TYPES.TOTAL_OUTSTANDING_SHARES,
    displayName: 'Total Outstanding Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalOutstandingSharesResolver' },
    metadata: {
      description: 'Total number of outstanding shares across all security types',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_FULLY_DILUTED_SHARES]: {
    id: COLUMN_TYPES.TOTAL_FULLY_DILUTED_SHARES,
    displayName: 'Total Fully Diluted Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalFullyDilutedSharesResolver' },
    metadata: {
      description: 'Total number of fully diluted shares (including options, warrants, convertibles)',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_PREFERRED_ROUNDS]: {
    id: COLUMN_TYPES.TOTAL_PREFERRED_ROUNDS,
    displayName: 'Total Preferred Rounds',
    displayFormat: DEFAULT_DISPLAY_FORMATS.integer,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalPreferredRoundsResolver' },
    metadata: {
      description: 'Total number of preferred equity rounds',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_OPTIONS]: {
    id: COLUMN_TYPES.TOTAL_OPTIONS,
    displayName: 'Total Options',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalOptionsResolver' },
    metadata: {
      description: 'Total number of outstanding options',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.AVG_OPTION_STRIKE_PRICE]: {
    id: COLUMN_TYPES.AVG_OPTION_STRIKE_PRICE,
    displayName: 'Avg Option Strike Price',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'avgOptionStrikePriceResolver' },
    metadata: {
      description: 'Weighted average strike price of all options',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.OPTION_POOL_PERCENT]: {
    id: COLUMN_TYPES.OPTION_POOL_PERCENT,
    displayName: 'Option Pool % of FD Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.TOTAL_OPTIONS, COLUMN_TYPES.TOTAL_FULLY_DILUTED_SHARES],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_fully_diluted_shares}=0, 0, {total_options}/{total_fully_diluted_shares})',
    },
    metadata: {
      description: 'Option pool as a percentage of total fully diluted shares',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_WARRANTS]: {
    id: COLUMN_TYPES.TOTAL_WARRANTS,
    displayName: 'Total Warrants',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalWarrantsResolver' },
    metadata: {
      description: 'Total number of outstanding warrants',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.AVG_WARRANT_STRIKE_PRICE]: {
    id: COLUMN_TYPES.AVG_WARRANT_STRIKE_PRICE,
    displayName: 'Avg Warrant Strike Price',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'avgWarrantStrikePriceResolver' },
    metadata: {
      description: 'Weighted average strike price of all warrants',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_UNISSUED_OPTIONS]: {
    id: COLUMN_TYPES.TOTAL_UNISSUED_OPTIONS,
    displayName: 'Total Unissued Options',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalUnissuedOptionsResolver' },
    metadata: {
      description: 'Total number of unissued/reserved option pool shares',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_COMMON_SHARES]: {
    id: COLUMN_TYPES.TOTAL_COMMON_SHARES,
    displayName: 'Total Common Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalCommonSharesResolver' },
    metadata: {
      description: 'Total number of common shares',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_OUTSTANDING_PREFERRED_SHARES]: {
    id: COLUMN_TYPES.TOTAL_OUTSTANDING_PREFERRED_SHARES,
    displayName: 'Total Outstanding Preferred Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalOutstandingPreferredSharesResolver' },
    metadata: {
      description: 'Total number of outstanding preferred shares',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_FULLY_DILUTED_PREFERRED_SHARES]: {
    id: COLUMN_TYPES.TOTAL_FULLY_DILUTED_PREFERRED_SHARES,
    displayName: 'Total FD Preferred Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalFullyDilutedPreferredSharesResolver' },
    metadata: {
      description: 'Total number of fully diluted preferred shares',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_RAISED]: {
    id: COLUMN_TYPES.TOTAL_RAISED,
    displayName: 'Total Raised',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalRaisedResolver' },
    metadata: {
      description: 'Total capital raised across all preferred rounds',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_INITIAL_LIQUIDATION_PREFERENCE]: {
    id: COLUMN_TYPES.TOTAL_INITIAL_LIQUIDATION_PREFERENCE,
    displayName: 'Total Initial Liquidation Preference',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalInitialLiquidationPreferenceResolver' },
    metadata: {
      description: 'Sum of initial liquidation preferences across all preferred securities',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_PREFERENCE_WITH_DIVIDENDS]: {
    id: COLUMN_TYPES.TOTAL_PREFERENCE_WITH_DIVIDENDS,
    displayName: 'Total Preference (with Dividends)',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalPreferenceWithDividendsResolver' },
    metadata: {
      description: 'Total liquidation preference including accrued dividends as of measurement date',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.INITIAL_NOTE_ISSUANCE_DATE]: {
    id: COLUMN_TYPES.INITIAL_NOTE_ISSUANCE_DATE,
    displayName: 'Initial Note Issuance Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'initialNoteIssuanceDateResolver' },
    metadata: {
      description: 'Date of the first convertible note issuance',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.INITIAL_NOTE_CONVERSION_MODEL]: {
    id: COLUMN_TYPES.INITIAL_NOTE_CONVERSION_MODEL,
    displayName: 'Initial Note Conversion Model',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'initialNoteConversionModelResolver' },
    metadata: {
      description: 'Conversion model of initial note (preferred, common, loan)',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.INITIAL_NOTE_CONVERSION_PRICE]: {
    id: COLUMN_TYPES.INITIAL_NOTE_CONVERSION_PRICE,
    displayName: 'Initial Note Conversion Price/Share',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'initialNoteConversionPriceResolver' },
    metadata: {
      description: 'Conversion price per share for the initial convertible note',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.INITIAL_NOTE_EXPECTED_SHARES]: {
    id: COLUMN_TYPES.INITIAL_NOTE_EXPECTED_SHARES,
    displayName: 'Initial Note Expected Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'initialNoteExpectedSharesResolver' },
    metadata: {
      description: 'Expected shares from initial convertible note upon conversion',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_NOTE_EXPECTED_SHARES]: {
    id: COLUMN_TYPES.TOTAL_NOTE_EXPECTED_SHARES,
    displayName: 'Total Note Expected Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalNoteExpectedSharesResolver' },
    metadata: {
      description: 'Total expected shares from all convertible notes upon conversion',
      category: 'cap_table',
      summaryType: 'both',
    },
  },

  // ============================================
  // Fund Ownership Columns
  // ============================================
  [COLUMN_TYPES.SECURITY_ID]: {
    id: COLUMN_TYPES.SECURITY_ID,
    displayName: 'Security ID',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'securityIdResolver' },
    metadata: {
      description: 'Identifier for the owned security',
      category: 'ownership',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.CASH_DISTRIBUTIONS]: {
    id: COLUMN_TYPES.CASH_DISTRIBUTIONS,
    displayName: 'Cash Distributions',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'cashDistributionsResolver' },
    metadata: {
      description: 'Total cash distributions received',
      category: 'ownership',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PROCEEDS_FROM_SHARES_SOLD]: {
    id: COLUMN_TYPES.PROCEEDS_FROM_SHARES_SOLD,
    displayName: 'Proceeds from Shares Sold',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'proceedsFromSharesSoldResolver' },
    metadata: {
      description: 'Total proceeds from shares sold (secondary sales)',
      category: 'ownership',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.LOAN_VALUE]: {
    id: COLUMN_TYPES.LOAN_VALUE,
    displayName: 'Loan Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'loanValueResolver' },
    metadata: {
      description: 'Current loan/note value',
      category: 'ownership',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Breakpoint Analysis Columns
  // ============================================
  [COLUMN_TYPES.USES_CUSTOM_BREAKPOINTS]: {
    id: COLUMN_TYPES.USES_CUSTOM_BREAKPOINTS,
    displayName: 'Uses Custom Breakpoints',
    displayFormat: DEFAULT_DISPLAY_FORMATS.boolean,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'usesCustomBreakpointsResolver' },
    metadata: {
      description: 'Whether the company uses custom breakpoints vs waterfall-derived',
      category: 'breakpoints',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.HIGHEST_BREAKPOINT_PRICE]: {
    id: COLUMN_TYPES.HIGHEST_BREAKPOINT_PRICE,
    displayName: 'Highest Breakpoint Price/Share',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'highestBreakpointPriceResolver' },
    metadata: {
      description: 'Price per common share at the highest breakpoint',
      category: 'breakpoints',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.HIGHEST_BREAKPOINT_LIMIT]: {
    id: COLUMN_TYPES.HIGHEST_BREAKPOINT_LIMIT,
    displayName: 'Highest Breakpoint Limit',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'highestBreakpointLimitResolver' },
    metadata: {
      description: 'Equity value limit at the highest breakpoint',
      category: 'breakpoints',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Cash Flow Columns
  // ============================================
  [COLUMN_TYPES.NET_CASH_FLOWS]: {
    id: COLUMN_TYPES.NET_CASH_FLOWS,
    displayName: 'Net Cash Flows',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'netCashFlowsResolver' },
    metadata: {
      description: 'Net cash flows (distributions minus investments)',
      category: 'cash_flows',
      summaryType: 'both',
    },
  },

  [COLUMN_TYPES.TOTAL_OTHER_CAPITAL_COSTS]: {
    id: COLUMN_TYPES.TOTAL_OTHER_CAPITAL_COSTS,
    displayName: 'Total Other Capital Costs',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalOtherCapitalCostsResolver' },
    metadata: {
      description: 'Total other capital costs manually entered in the cash flows page',
      category: 'cash_flows',
      summaryType: 'both',
    },
  },

  // ============================================
  // Financials - Date Columns
  // ============================================
  [COLUMN_TYPES.FINANCIALS_DATE]: {
    id: COLUMN_TYPES.FINANCIALS_DATE,
    displayName: 'Financials Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'financialsDateResolver' },
    metadata: {
      description: 'Date of the financial statements',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.LTM_DATE]: {
    id: COLUMN_TYPES.LTM_DATE,
    displayName: 'LTM Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ltmDateResolver' },
    metadata: {
      description: 'Last twelve months period end date',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.NTM_DATE]: {
    id: COLUMN_TYPES.NTM_DATE,
    displayName: 'NTM Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ntmDateResolver' },
    metadata: {
      description: 'Next twelve months period end date',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BALANCE_SHEET_DATE]: {
    id: COLUMN_TYPES.BALANCE_SHEET_DATE,
    displayName: 'Balance Sheet As Of Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'balanceSheetDateResolver' },
    metadata: {
      description: 'As-of date for the balance sheet',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Financials - Income Statement (LTM/NTM)
  // ============================================
  [COLUMN_TYPES.TOTAL_REVENUE]: {
    id: COLUMN_TYPES.TOTAL_REVENUE,
    displayName: 'Total Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalRevenueResolver' },
    metadata: {
      description: 'Total revenue (LTM or NTM)',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TOTAL_COST_OF_SALES]: {
    id: COLUMN_TYPES.TOTAL_COST_OF_SALES,
    displayName: 'Total Cost of Sales',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalCostOfSalesResolver' },
    metadata: {
      description: 'Total cost of sales / COGS (LTM or NTM)',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GROSS_PROFIT]: {
    id: COLUMN_TYPES.GROSS_PROFIT,
    displayName: 'Gross Profit',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'grossProfitResolver' },
    metadata: {
      description: 'Gross profit (Revenue - COGS)',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.OPERATING_EXPENSES]: {
    id: COLUMN_TYPES.OPERATING_EXPENSES,
    displayName: 'Operating Expenses',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'operatingExpensesResolver' },
    metadata: {
      description: 'Total operating expenses',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.EBITDA]: {
    id: COLUMN_TYPES.EBITDA,
    displayName: 'EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ebitdaResolver' },
    metadata: {
      description: 'Earnings before interest, taxes, depreciation, and amortization',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.ADJUSTED_EBITDA]: {
    id: COLUMN_TYPES.ADJUSTED_EBITDA,
    displayName: 'Adjusted EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'adjustedEbitdaResolver' },
    metadata: {
      description: 'Adjusted EBITDA (excluding non-recurring items)',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DEPRECIATION_EXPENSE]: {
    id: COLUMN_TYPES.DEPRECIATION_EXPENSE,
    displayName: 'Depreciation Expense',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'depreciationExpenseResolver' },
    metadata: {
      description: 'Total depreciation expense',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.AMORTIZATION_EXPENSE]: {
    id: COLUMN_TYPES.AMORTIZATION_EXPENSE,
    displayName: 'Amortization Expense',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'amortizationExpenseResolver' },
    metadata: {
      description: 'Total amortization expense',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.EBIT]: {
    id: COLUMN_TYPES.EBIT,
    displayName: 'EBIT',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ebitResolver' },
    metadata: {
      description: 'Earnings before interest and taxes',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.INTEREST_EXPENSE]: {
    id: COLUMN_TYPES.INTEREST_EXPENSE,
    displayName: 'Interest Expense',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'interestExpenseResolver' },
    metadata: {
      description: 'Total interest expense',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.OTHER_EXPENSE]: {
    id: COLUMN_TYPES.OTHER_EXPENSE,
    displayName: 'Other Expense',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'otherExpenseResolver' },
    metadata: {
      description: 'Other non-operating expenses',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.PRETAX_INCOME]: {
    id: COLUMN_TYPES.PRETAX_INCOME,
    displayName: 'Pretax Income',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'pretaxIncomeResolver' },
    metadata: {
      description: 'Income before income taxes',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.INCOME_TAXES]: {
    id: COLUMN_TYPES.INCOME_TAXES,
    displayName: 'Income Taxes',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'incomeTaxesResolver' },
    metadata: {
      description: 'Total income tax expense',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.NET_INCOME]: {
    id: COLUMN_TYPES.NET_INCOME,
    displayName: 'Net Income',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'netIncomeResolver' },
    metadata: {
      description: 'Net income after all expenses and taxes',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Financials - Margins & Ratios
  // ============================================
  [COLUMN_TYPES.REVENUE_GROWTH_RATE]: {
    id: COLUMN_TYPES.REVENUE_GROWTH_RATE,
    displayName: 'Revenue Growth Rate',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'revenueGrowthRateResolver' },
    metadata: {
      description: 'Year-over-year revenue growth rate',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.COST_OF_SALES_PERCENT]: {
    id: COLUMN_TYPES.COST_OF_SALES_PERCENT,
    displayName: 'Cost of Sales %',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.TOTAL_COST_OF_SALES, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {total_cost_of_sales}/{total_revenue})',
    },
    metadata: {
      description: 'Cost of sales as a percentage of revenue',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GROSS_MARGIN]: {
    id: COLUMN_TYPES.GROSS_MARGIN,
    displayName: 'Gross Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.GROSS_PROFIT, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {gross_profit}/{total_revenue})',
    },
    metadata: {
      description: 'Gross profit as a percentage of revenue',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.OPERATING_PERCENT_OF_SALES]: {
    id: COLUMN_TYPES.OPERATING_PERCENT_OF_SALES,
    displayName: 'Operating % of Sales',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.OPERATING_EXPENSES, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {operating_expenses}/{total_revenue})',
    },
    metadata: {
      description: 'Operating expenses as a percentage of sales',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.EBITDA_MARGIN]: {
    id: COLUMN_TYPES.EBITDA_MARGIN,
    displayName: 'EBITDA Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.EBITDA, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {ebitda}/{total_revenue})',
    },
    metadata: {
      description: 'EBITDA as a percentage of revenue',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.ADJUSTED_EBITDA_MARGIN]: {
    id: COLUMN_TYPES.ADJUSTED_EBITDA_MARGIN,
    displayName: 'Adjusted EBITDA Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.ADJUSTED_EBITDA, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {adjusted_ebitda}/{total_revenue})',
    },
    metadata: {
      description: 'Adjusted EBITDA as a percentage of revenue',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.NET_PROFIT_MARGIN]: {
    id: COLUMN_TYPES.NET_PROFIT_MARGIN,
    displayName: 'Net Profit Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.NET_INCOME, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {net_income}/{total_revenue})',
    },
    metadata: {
      description: 'Net income as a percentage of revenue',
      category: 'financials',
      summaryType: 'firm',
    },
  },

  // ============================================
  // Balance Sheet - Assets
  // ============================================
  [COLUMN_TYPES.CASH_AND_EQUIVALENTS]: {
    id: COLUMN_TYPES.CASH_AND_EQUIVALENTS,
    displayName: 'Cash and Equivalents',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'cashAndEquivalentsResolver' },
    metadata: { description: 'Cash and cash equivalents', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.ACCOUNTS_RECEIVABLES]: {
    id: COLUMN_TYPES.ACCOUNTS_RECEIVABLES,
    displayName: 'Accounts Receivables',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'accountsReceivablesResolver' },
    metadata: { description: 'Accounts receivable', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.INVENTORY]: {
    id: COLUMN_TYPES.INVENTORY,
    displayName: 'Inventory',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'inventoryResolver' },
    metadata: { description: 'Inventory value', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.OTHER_CURRENT_ASSETS]: {
    id: COLUMN_TYPES.OTHER_CURRENT_ASSETS,
    displayName: 'Other Current Assets',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'otherCurrentAssetsResolver' },
    metadata: { description: 'Other current assets', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_CURRENT_ASSETS]: {
    id: COLUMN_TYPES.TOTAL_CURRENT_ASSETS,
    displayName: 'Total Current Assets',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalCurrentAssetsResolver' },
    metadata: { description: 'Total current assets', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.PPE]: {
    id: COLUMN_TYPES.PPE,
    displayName: 'PP&E',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ppeResolver' },
    metadata: { description: 'Property, plant, and equipment', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.INTANGIBLES]: {
    id: COLUMN_TYPES.INTANGIBLES,
    displayName: 'Intangibles',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'intangiblesResolver' },
    metadata: { description: 'Intangible assets', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.OTHER_LONG_TERM_ASSETS]: {
    id: COLUMN_TYPES.OTHER_LONG_TERM_ASSETS,
    displayName: 'Other Long Term Assets',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'otherLongTermAssetsResolver' },
    metadata: { description: 'Other long-term assets', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_LONG_TERM_ASSETS]: {
    id: COLUMN_TYPES.TOTAL_LONG_TERM_ASSETS,
    displayName: 'Total Long Term Assets',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalLongTermAssetsResolver' },
    metadata: { description: 'Total long-term assets', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_ASSETS]: {
    id: COLUMN_TYPES.TOTAL_ASSETS,
    displayName: 'Total Assets',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalAssetsResolver' },
    metadata: { description: 'Total assets', category: 'financials', summaryType: 'firm' },
  },

  // ============================================
  // Balance Sheet - Liabilities & Equity
  // ============================================
  [COLUMN_TYPES.SHORT_TERM_DEBT]: {
    id: COLUMN_TYPES.SHORT_TERM_DEBT,
    displayName: 'Short Term Debt',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'shortTermDebtResolver' },
    metadata: { description: 'Short-term debt', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.ACCOUNTS_PAYABLE]: {
    id: COLUMN_TYPES.ACCOUNTS_PAYABLE,
    displayName: 'Accounts Payable',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'accountsPayableResolver' },
    metadata: { description: 'Accounts payable', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.ACCRUED_LIABILITIES]: {
    id: COLUMN_TYPES.ACCRUED_LIABILITIES,
    displayName: 'Accrued Liabilities',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'accruedLiabilitiesResolver' },
    metadata: { description: 'Accrued liabilities', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.DEFERRED_REVENUE]: {
    id: COLUMN_TYPES.DEFERRED_REVENUE,
    displayName: 'Deferred Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'deferredRevenueResolver' },
    metadata: { description: 'Deferred revenue', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.OTHER_CURRENT_LIABILITIES]: {
    id: COLUMN_TYPES.OTHER_CURRENT_LIABILITIES,
    displayName: 'Other Current Liabilities',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'otherCurrentLiabilitiesResolver' },
    metadata: { description: 'Other current liabilities', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_CURRENT_LIABILITIES]: {
    id: COLUMN_TYPES.TOTAL_CURRENT_LIABILITIES,
    displayName: 'Total Current Liabilities',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalCurrentLiabilitiesResolver' },
    metadata: { description: 'Total current liabilities', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.LONG_TERM_DEBT]: {
    id: COLUMN_TYPES.LONG_TERM_DEBT,
    displayName: 'Long Term Debt',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'longTermDebtResolver' },
    metadata: { description: 'Long-term debt', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.OTHER_LONG_TERM_DEBT]: {
    id: COLUMN_TYPES.OTHER_LONG_TERM_DEBT,
    displayName: 'Other Long Term Debt',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'otherLongTermDebtResolver' },
    metadata: { description: 'Other long-term debt obligations', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_LONG_TERM_DEBT]: {
    id: COLUMN_TYPES.TOTAL_LONG_TERM_DEBT,
    displayName: 'Total Long Term Debt',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalLongTermDebtResolver' },
    metadata: { description: 'Total long-term debt', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_LIABILITIES]: {
    id: COLUMN_TYPES.TOTAL_LIABILITIES,
    displayName: 'Total Liabilities',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalLiabilitiesResolver' },
    metadata: { description: 'Total liabilities', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BS_EQUITY]: {
    id: COLUMN_TYPES.BS_EQUITY,
    displayName: 'Equity (Balance Sheet)',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'bsEquityResolver' },
    metadata: { description: 'Shareholders\' equity from balance sheet', category: 'financials', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TOTAL_LIABILITIES_AND_EQUITY]: {
    id: COLUMN_TYPES.TOTAL_LIABILITIES_AND_EQUITY,
    displayName: 'Total Liabilities and Equity',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalLiabilitiesAndEquityResolver' },
    metadata: { description: 'Total liabilities plus equity', category: 'financials', summaryType: 'firm' },
  },

  // ============================================
  // Implied Multiples
  // ============================================
  [COLUMN_TYPES.IMPLIED_EV_LTM_REVENUE]: {
    id: COLUMN_TYPES.IMPLIED_EV_LTM_REVENUE,
    displayName: 'Implied EV/LTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.ENTERPRISE_VALUE, COLUMN_TYPES.TOTAL_REVENUE],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({total_revenue}=0, 0, {enterprise_value}/{total_revenue})',
    },
    metadata: {
      description: 'Implied enterprise value to LTM revenue multiple',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.IMPLIED_EV_LTM_EBITDA]: {
    id: COLUMN_TYPES.IMPLIED_EV_LTM_EBITDA,
    displayName: 'Implied EV/LTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'virtual',
    dependsOn: [COLUMN_TYPES.ENTERPRISE_VALUE, COLUMN_TYPES.EBITDA],
    calculation: {
      strategy: 'excel-formula',
      template: '=IF({ebitda}=0, 0, {enterprise_value}/{ebitda})',
    },
    metadata: {
      description: 'Implied enterprise value to LTM EBITDA multiple',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.IMPLIED_EV_NTM_REVENUE]: {
    id: COLUMN_TYPES.IMPLIED_EV_NTM_REVENUE,
    displayName: 'Implied EV/NTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'impliedEvNtmRevenueResolver' },
    metadata: {
      description: 'Implied enterprise value to NTM revenue multiple',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.IMPLIED_EV_NTM_EBITDA]: {
    id: COLUMN_TYPES.IMPLIED_EV_NTM_EBITDA,
    displayName: 'Implied EV/NTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'impliedEvNtmEbitdaResolver' },
    metadata: {
      description: 'Implied enterprise value to NTM EBITDA multiple',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.IMPLIED_EV_KPI]: {
    id: COLUMN_TYPES.IMPLIED_EV_KPI,
    displayName: 'Implied EV/KPI Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'impliedEvKpiResolver' },
    metadata: {
      description: 'Implied enterprise value to KPI metric multiple',
      category: 'metrics',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // ============================================
  // GPC (Guideline Public Companies)
  // ============================================
  [COLUMN_TYPES.GPC_USE_MULTIPLE_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_USE_MULTIPLE_PREMIUM_DISCOUNT,
    displayName: 'GPC: Use Multiple Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.boolean,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcUseMultiplePremiumDiscountResolver' },
    metadata: { description: 'Whether GPC uses multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_USE_EQUITY_VALUE]: {
    id: COLUMN_TYPES.GPC_USE_EQUITY_VALUE,
    displayName: 'GPC: Use Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.boolean,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcUseEquityValueResolver' },
    metadata: { description: 'Whether GPC derives equity value directly', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_USE_CALCULATED_FORWARD_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_USE_CALCULATED_FORWARD_MULTIPLE,
    displayName: 'GPC: Use Calculated Forward Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.boolean,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcUseCalculatedForwardMultipleResolver' },
    metadata: {
      description: 'Whether GPC uses calculated forward multiple',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_FIRST_MULTIPLE_TYPE]: {
    id: COLUMN_TYPES.GPC_FIRST_MULTIPLE_TYPE,
    displayName: 'GPC: First Multiple Type',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcFirstMultipleTypeResolver' },
    metadata: {
      description: 'First multiple type (LTM, NTM, or period year)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_SECOND_MULTIPLE_TYPE]: {
    id: COLUMN_TYPES.GPC_SECOND_MULTIPLE_TYPE,
    displayName: 'GPC: Second Multiple Type',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcSecondMultipleTypeResolver' },
    metadata: {
      description: 'Second multiple type (LTM, NTM, or period year)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_LTM_REVENUE_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_LTM_REVENUE_APPLIED_MULTIPLE,
    displayName: 'GPC: LTM Revenue Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcLtmRevenueAppliedMultipleResolver' },
    metadata: {
      description: 'GPC LTM revenue applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_LTM_EBITDA_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_LTM_EBITDA_APPLIED_MULTIPLE,
    displayName: 'GPC: LTM EBITDA Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcLtmEbitdaAppliedMultipleResolver' },
    metadata: {
      description: 'GPC LTM EBITDA applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_NTM_REVENUE_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_NTM_REVENUE_APPLIED_MULTIPLE,
    displayName: 'GPC: NTM Revenue Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcNtmRevenueAppliedMultipleResolver' },
    metadata: {
      description: 'GPC NTM revenue applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_NTM_EBITDA_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_NTM_EBITDA_APPLIED_MULTIPLE,
    displayName: 'GPC: NTM EBITDA Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcNtmEbitdaAppliedMultipleResolver' },
    metadata: {
      description: 'GPC NTM EBITDA applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_WEIGHTED_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.GPC_WEIGHTED_APPLIED_MULTIPLE,
    displayName: 'GPC: Weighted Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcWeightedAppliedMultipleResolver' },
    metadata: {
      description: 'GPC weighted applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_LTM_REVENUE_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_LTM_REVENUE_PREMIUM_DISCOUNT,
    displayName: 'GPC: LTM Revenue Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcLtmRevenuePremiumDiscountResolver' },
    metadata: { description: 'GPC LTM revenue multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_LTM_EBITDA_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_LTM_EBITDA_PREMIUM_DISCOUNT,
    displayName: 'GPC: LTM EBITDA Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcLtmEbitdaPremiumDiscountResolver' },
    metadata: { description: 'GPC LTM EBITDA multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_NTM_REVENUE_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_NTM_REVENUE_PREMIUM_DISCOUNT,
    displayName: 'GPC: NTM Revenue Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcNtmRevenuePremiumDiscountResolver' },
    metadata: { description: 'GPC NTM revenue multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_NTM_EBITDA_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_NTM_EBITDA_PREMIUM_DISCOUNT,
    displayName: 'GPC: NTM EBITDA Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcNtmEbitdaPremiumDiscountResolver' },
    metadata: { description: 'GPC NTM EBITDA multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_WEIGHTED_PREMIUM_DISCOUNT]: {
    id: COLUMN_TYPES.GPC_WEIGHTED_PREMIUM_DISCOUNT,
    displayName: 'GPC: Weighted Premium/Discount',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcWeightedPremiumDiscountResolver' },
    metadata: { description: 'GPC weighted multiple premium/discount', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_WEIGHTED_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.GPC_WEIGHTED_ENTERPRISE_VALUE,
    displayName: 'GPC: Weighted Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcWeightedEnterpriseValueResolver' },
    metadata: {
      description: 'GPC weighted enterprise value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_WEIGHTED_EQUITY_VALUE]: {
    id: COLUMN_TYPES.GPC_WEIGHTED_EQUITY_VALUE,
    displayName: 'GPC: Weighted Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcWeightedEquityValueResolver' },
    metadata: {
      description: 'GPC weighted equity value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_ALL_TICKERS]: {
    id: COLUMN_TYPES.GPC_ALL_TICKERS,
    displayName: 'GPC: All Tickers',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcAllTickersResolver' },
    metadata: { description: 'All GPC ticker symbols', category: 'valuation', summaryType: 'firm' },
  },

  // GPC Percentile Rankings
  [COLUMN_TYPES.GPC_PERCENTILE_LTM_REVENUE]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_LTM_REVENUE,
    displayName: 'GPC Percentile: LTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileLtmRevenueResolver' },
    metadata: { description: 'GPC percentile ranking for LTM revenue', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_LTM_REVENUE_GROWTH]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_LTM_REVENUE_GROWTH,
    displayName: 'GPC Percentile: LTM Revenue Growth',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileLtmRevenueGrowthResolver' },
    metadata: {
      description: 'GPC percentile ranking for LTM revenue growth',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_NTM_REVENUE]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_NTM_REVENUE,
    displayName: 'GPC Percentile: NTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileNtmRevenueResolver' },
    metadata: { description: 'GPC percentile ranking for NTM revenue', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_NTM_REVENUE_GROWTH]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_NTM_REVENUE_GROWTH,
    displayName: 'GPC Percentile: NTM Revenue Growth',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileNtmRevenueGrowthResolver' },
    metadata: {
      description: 'GPC percentile ranking for NTM revenue growth',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_LTM_EBITDA]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_LTM_EBITDA,
    displayName: 'GPC Percentile: LTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileLtmEbitdaResolver' },
    metadata: { description: 'GPC percentile ranking for LTM EBITDA', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_NTM_EBITDA]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_NTM_EBITDA,
    displayName: 'GPC Percentile: NTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileNtmEbitdaResolver' },
    metadata: { description: 'GPC percentile ranking for NTM EBITDA', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_GROSS_MARGIN]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_GROSS_MARGIN,
    displayName: 'GPC Percentile: Gross Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileGrossMarginResolver' },
    metadata: { description: 'GPC percentile ranking for gross margin', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_EBITDA_MARGIN]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_EBITDA_MARGIN,
    displayName: 'GPC Percentile: EBITDA Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileEbitdaMarginResolver' },
    metadata: { description: 'GPC percentile ranking for EBITDA margin', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_TANGIBLE_BOOK_VALUE]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_TANGIBLE_BOOK_VALUE,
    displayName: 'GPC Percentile: Tangible Book Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileTangibleBookValueResolver' },
    metadata: {
      description: 'GPC percentile ranking for tangible book value',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_PERCENTILE_NET_INCOME]: {
    id: COLUMN_TYPES.GPC_PERCENTILE_NET_INCOME,
    displayName: 'GPC Percentile: Net Income',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcPercentileNetIncomeResolver' },
    metadata: { description: 'GPC percentile ranking for net income', category: 'valuation', summaryType: 'firm' },
  },

  // GPC Volatility
  [COLUMN_TYPES.GPC_SELECTED_1Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_SELECTED_1Y_VOLATILITY,
    displayName: 'GPC: Selected 1Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcSelected1yVolatilityResolver' },
    metadata: {
      description: 'Selected 1-year volatility measure (median, mean, etc.)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_1Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_1Y_VOLATILITY,
    displayName: 'GPC: 1Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpc1yVolatilityResolver' },
    metadata: { description: '1-year equity volatility from GPC set', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_SELECTED_2Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_SELECTED_2Y_VOLATILITY,
    displayName: 'GPC: Selected 2Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcSelected2yVolatilityResolver' },
    metadata: {
      description: 'Selected 2-year volatility measure (median, mean, etc.)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_2Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_2Y_VOLATILITY,
    displayName: 'GPC: 2Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpc2yVolatilityResolver' },
    metadata: { description: '2-year equity volatility from GPC set', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.GPC_SELECTED_5Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_SELECTED_5Y_VOLATILITY,
    displayName: 'GPC: Selected 5Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpcSelected5yVolatilityResolver' },
    metadata: {
      description: 'Selected 5-year volatility measure (median, mean, etc.)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.GPC_5Y_VOLATILITY]: {
    id: COLUMN_TYPES.GPC_5Y_VOLATILITY,
    displayName: 'GPC: 5Y Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'gpc5yVolatilityResolver' },
    metadata: { description: '5-year equity volatility from GPC set', category: 'valuation', summaryType: 'firm' },
  },

  // ============================================
  // Transaction Comps
  // ============================================
  [COLUMN_TYPES.TC_LTM_REVENUE_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.TC_LTM_REVENUE_APPLIED_MULTIPLE,
    displayName: 'TC: LTM Revenue Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcLtmRevenueAppliedMultipleResolver' },
    metadata: {
      description: 'Transaction comps LTM revenue applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_LTM_EBITDA_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.TC_LTM_EBITDA_APPLIED_MULTIPLE,
    displayName: 'TC: LTM EBITDA Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcLtmEbitdaAppliedMultipleResolver' },
    metadata: {
      description: 'Transaction comps LTM EBITDA applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_NTM_REVENUE_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.TC_NTM_REVENUE_APPLIED_MULTIPLE,
    displayName: 'TC: NTM Revenue Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcNtmRevenueAppliedMultipleResolver' },
    metadata: {
      description: 'Transaction comps NTM revenue applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_NTM_EBITDA_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.TC_NTM_EBITDA_APPLIED_MULTIPLE,
    displayName: 'TC: NTM EBITDA Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcNtmEbitdaAppliedMultipleResolver' },
    metadata: {
      description: 'Transaction comps NTM EBITDA applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_WEIGHTED_APPLIED_MULTIPLE]: {
    id: COLUMN_TYPES.TC_WEIGHTED_APPLIED_MULTIPLE,
    displayName: 'TC: Weighted Applied Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcWeightedAppliedMultipleResolver' },
    metadata: {
      description: 'Transaction comps weighted applied multiple',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_WEIGHTED_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.TC_WEIGHTED_ENTERPRISE_VALUE,
    displayName: 'TC: Weighted Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcWeightedEnterpriseValueResolver' },
    metadata: {
      description: 'Transaction comps weighted enterprise value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_WEIGHTED_EQUITY_VALUE]: {
    id: COLUMN_TYPES.TC_WEIGHTED_EQUITY_VALUE,
    displayName: 'TC: Weighted Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcWeightedEquityValueResolver' },
    metadata: {
      description: 'Transaction comps weighted equity value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // TC Percentile Rankings
  [COLUMN_TYPES.TC_PERCENTILE_LTM_REVENUE]: {
    id: COLUMN_TYPES.TC_PERCENTILE_LTM_REVENUE,
    displayName: 'TC Percentile: LTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileLtmRevenueResolver' },
    metadata: { description: 'TC percentile ranking for LTM revenue', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TC_PERCENTILE_LTM_REVENUE_GROWTH]: {
    id: COLUMN_TYPES.TC_PERCENTILE_LTM_REVENUE_GROWTH,
    displayName: 'TC Percentile: LTM Revenue Growth',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileLtmRevenueGrowthResolver' },
    metadata: {
      description: 'TC percentile ranking for LTM revenue growth',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_PERCENTILE_NTM_REVENUE]: {
    id: COLUMN_TYPES.TC_PERCENTILE_NTM_REVENUE,
    displayName: 'TC Percentile: NTM Revenue',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileNtmRevenueResolver' },
    metadata: { description: 'TC percentile ranking for NTM revenue', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TC_PERCENTILE_NTM_REVENUE_GROWTH]: {
    id: COLUMN_TYPES.TC_PERCENTILE_NTM_REVENUE_GROWTH,
    displayName: 'TC Percentile: NTM Revenue Growth',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileNtmRevenueGrowthResolver' },
    metadata: {
      description: 'TC percentile ranking for NTM revenue growth',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.TC_PERCENTILE_LTM_EBITDA]: {
    id: COLUMN_TYPES.TC_PERCENTILE_LTM_EBITDA,
    displayName: 'TC Percentile: LTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileLtmEbitdaResolver' },
    metadata: { description: 'TC percentile ranking for LTM EBITDA', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TC_PERCENTILE_NTM_EBITDA]: {
    id: COLUMN_TYPES.TC_PERCENTILE_NTM_EBITDA,
    displayName: 'TC Percentile: NTM EBITDA',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileNtmEbitdaResolver' },
    metadata: { description: 'TC percentile ranking for NTM EBITDA', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TC_PERCENTILE_GROSS_MARGIN]: {
    id: COLUMN_TYPES.TC_PERCENTILE_GROSS_MARGIN,
    displayName: 'TC Percentile: Gross Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileGrossMarginResolver' },
    metadata: { description: 'TC percentile ranking for gross margin', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.TC_PERCENTILE_EBITDA_MARGIN]: {
    id: COLUMN_TYPES.TC_PERCENTILE_EBITDA_MARGIN,
    displayName: 'TC Percentile: EBITDA Margin',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'tcPercentileEbitdaMarginResolver' },
    metadata: { description: 'TC percentile ranking for EBITDA margin', category: 'valuation', summaryType: 'firm' },
  },

  // ============================================
  // Backsolve
  // ============================================
  [COLUMN_TYPES.BACKSOLVE_ALLOCATION_METHODS]: {
    id: COLUMN_TYPES.BACKSOLVE_ALLOCATION_METHODS,
    displayName: 'Backsolve: Allocation Methods',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveAllocationMethodsResolver' },
    metadata: {
      description: 'Backsolve allocation methods (CSE, Waterfall, OPM)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_SECURITY_NAMES]: {
    id: COLUMN_TYPES.BACKSOLVE_SECURITY_NAMES,
    displayName: 'Backsolve: Security Names',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveSecurityNamesResolver' },
    metadata: {
      description: 'Security names used in backsolve (e.g., Series A, Series B)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_TARGET_TOTAL_SHARES]: {
    id: COLUMN_TYPES.BACKSOLVE_TARGET_TOTAL_SHARES,
    displayName: 'Backsolve: Target Total Shares',
    displayFormat: DEFAULT_DISPLAY_FORMATS.shares,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveTargetTotalSharesResolver' },
    metadata: { description: 'Target total shares in backsolve', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_TARGET_TOTAL_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_TARGET_TOTAL_VALUE,
    displayName: 'Backsolve: Target Total Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveTargetTotalValueResolver' },
    metadata: {
      description: 'Target total value in backsolve',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_OPM_DATE]: {
    id: COLUMN_TYPES.BACKSOLVE_OPM_DATE,
    displayName: 'Backsolve: OPM Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveOpmDateResolver' },
    metadata: { description: 'OPM backsolve date', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_OPM_VOLATILITY]: {
    id: COLUMN_TYPES.BACKSOLVE_OPM_VOLATILITY,
    displayName: 'Backsolve: OPM Volatility',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveOpmVolatilityResolver' },
    metadata: { description: 'OPM volatility selected for backsolve', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_OPM_MATURITY]: {
    id: COLUMN_TYPES.BACKSOLVE_OPM_MATURITY,
    displayName: 'Backsolve: OPM Maturity',
    displayFormat: DEFAULT_DISPLAY_FORMATS.number,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveOpmMaturityResolver' },
    metadata: { description: 'OPM maturity in years', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_OPM_MATURITY_DATE]: {
    id: COLUMN_TYPES.BACKSOLVE_OPM_MATURITY_DATE,
    displayName: 'Backsolve: OPM Maturity Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveOpmMaturityDateResolver' },
    metadata: { description: 'OPM maturity date', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_OPM_RISK_FREE_RATE]: {
    id: COLUMN_TYPES.BACKSOLVE_OPM_RISK_FREE_RATE,
    displayName: 'Backsolve: OPM Risk-Free Rate',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveOpmRiskFreeRateResolver' },
    metadata: { description: 'OPM risk-free rate', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_IMPLIED_EQUITY_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_IMPLIED_EQUITY_VALUE,
    displayName: 'Backsolve: Implied Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveImpliedEquityValueResolver' },
    metadata: {
      description: 'Implied equity value from backsolve',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_ENTERPRISE_VALUE,
    displayName: 'Backsolve: Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveEnterpriseValueResolver' },
    metadata: {
      description: 'Enterprise value from backsolve',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_USES_MARKET_ADJUSTMENT]: {
    id: COLUMN_TYPES.BACKSOLVE_USES_MARKET_ADJUSTMENT,
    displayName: 'Backsolve: Uses Market Adjustment',
    displayFormat: DEFAULT_DISPLAY_FORMATS.boolean,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveUsesMarketAdjustmentResolver' },
    metadata: { description: 'Whether backsolve uses market adjustment', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_MARKET_ADJUSTMENT_DATE]: {
    id: COLUMN_TYPES.BACKSOLVE_MARKET_ADJUSTMENT_DATE,
    displayName: 'Backsolve: Market Adjustment Date',
    displayFormat: DEFAULT_DISPLAY_FORMATS.dateShort,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveMarketAdjustmentDateResolver' },
    metadata: { description: 'Market adjustment as-of date', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_METRIC_BASIS]: {
    id: COLUMN_TYPES.BACKSOLVE_METRIC_BASIS,
    displayName: 'Backsolve: Metric Basis',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveMetricBasisResolver' },
    metadata: {
      description: 'Metric basis for adjustment (Market Cap, Enterprise Value, etc.)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_MARKET_ADJUSTMENT]: {
    id: COLUMN_TYPES.BACKSOLVE_MARKET_ADJUSTMENT,
    displayName: 'Backsolve: Market Adjustment %',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveMarketAdjustmentResolver' },
    metadata: { description: 'Market adjustment percentage', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.BACKSOLVE_ADJUSTED_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_ADJUSTED_VALUE,
    displayName: 'Backsolve: Adjusted Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveAdjustedValueResolver' },
    metadata: {
      description: 'Adjusted value (equity or enterprise) after market adjustment',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_ADJUSTED_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_ADJUSTED_ENTERPRISE_VALUE,
    displayName: 'Backsolve: Adjusted Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveAdjustedEnterpriseValueResolver' },
    metadata: {
      description: 'Adjusted enterprise value after market adjustment',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.BACKSOLVE_ADJUSTED_EQUITY_VALUE]: {
    id: COLUMN_TYPES.BACKSOLVE_ADJUSTED_EQUITY_VALUE,
    displayName: 'Backsolve: Adjusted Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'backsolveAdjustedEquityValueResolver' },
    metadata: {
      description: 'Adjusted equity value after market adjustment',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // ============================================
  // External Value
  // ============================================
  [COLUMN_TYPES.EXTERNAL_EQUITY_VALUE]: {
    id: COLUMN_TYPES.EXTERNAL_EQUITY_VALUE,
    displayName: 'External: Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'externalEquityValueResolver' },
    metadata: {
      description: 'Externally provided equity value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.EXTERNAL_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.EXTERNAL_ENTERPRISE_VALUE,
    displayName: 'External: Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'externalEnterpriseValueResolver' },
    metadata: {
      description: 'Externally provided enterprise value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // ============================================
  // DCF (Detailed)
  // ============================================
  [COLUMN_TYPES.DCF_WACC]: {
    id: COLUMN_TYPES.DCF_WACC,
    displayName: 'DCF: WACC',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'field', path: 'dcf_discount_rate' },
    metadata: {
      description: 'Weighted average cost of capital (DCF discount rate)',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DCF_EFFECTIVE_TAX_RATE]: {
    id: COLUMN_TYPES.DCF_EFFECTIVE_TAX_RATE,
    displayName: 'DCF: Effective Tax Rate',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'dcfEffectiveTaxRateResolver' },
    metadata: { description: 'Effective tax rate used in DCF', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.DCF_PV_DISCRETE_CASH_FLOWS]: {
    id: COLUMN_TYPES.DCF_PV_DISCRETE_CASH_FLOWS,
    displayName: 'DCF: PV of Discrete Cash Flows',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'dcfPvDiscreteCashFlowsResolver' },
    metadata: {
      description: 'Summed present value of discrete period cash flows',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DCF_PV_TERMINAL_VALUE]: {
    id: COLUMN_TYPES.DCF_PV_TERMINAL_VALUE,
    displayName: 'DCF: PV of Terminal Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'dcfPvTerminalValueResolver' },
    metadata: {
      description: 'Present value of terminal value',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DCF_PV_NOL_CARRYFORWARDS]: {
    id: COLUMN_TYPES.DCF_PV_NOL_CARRYFORWARDS,
    displayName: 'DCF: PV of NOL Carryforwards',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'dcfPvNolCarryforwardsResolver' },
    metadata: {
      description: 'Present value of net operating loss carryforwards',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.DCF_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.DCF_ENTERPRISE_VALUE,
    displayName: 'DCF: Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'dcfEnterpriseValueResolver' },
    metadata: {
      description: 'Enterprise value derived from DCF',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // ============================================
  // Specified Share Value
  // ============================================
  [COLUMN_TYPES.SSV_EQUITY_VALUE]: {
    id: COLUMN_TYPES.SSV_EQUITY_VALUE,
    displayName: 'SSV: Equity Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ssvEquityValueResolver' },
    metadata: {
      description: 'Equity value from specified share value approach',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.SSV_ENTERPRISE_VALUE]: {
    id: COLUMN_TYPES.SSV_ENTERPRISE_VALUE,
    displayName: 'SSV: Enterprise Value',
    displayFormat: DEFAULT_DISPLAY_FORMATS.currency,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'ssvEnterpriseValueResolver' },
    metadata: {
      description: 'Enterprise value from specified share value approach',
      category: 'valuation',
      isDraftSensitive: true,
      summaryType: 'firm',
    },
  },

  // ============================================
  // Calibration
  // ============================================
  [COLUMN_TYPES.CAL_LTM_REVENUE_MULTIPLE]: {
    id: COLUMN_TYPES.CAL_LTM_REVENUE_MULTIPLE,
    displayName: 'Calibration: LTM Revenue Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmRevenueMultipleResolver' },
    metadata: { description: 'Calibration LTM revenue multiple', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_NTM_REVENUE_MULTIPLE]: {
    id: COLUMN_TYPES.CAL_NTM_REVENUE_MULTIPLE,
    displayName: 'Calibration: NTM Revenue Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmRevenueMultipleResolver' },
    metadata: { description: 'Calibration NTM revenue multiple', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_LTM_REVENUE_PERCENTILE]: {
    id: COLUMN_TYPES.CAL_LTM_REVENUE_PERCENTILE,
    displayName: 'Calibration: LTM Revenue Percentile',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmRevenuePercentileResolver' },
    metadata: {
      description: 'Calibration LTM revenue multiple percentile',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.CAL_NTM_REVENUE_PERCENTILE]: {
    id: COLUMN_TYPES.CAL_NTM_REVENUE_PERCENTILE,
    displayName: 'Calibration: NTM Revenue Percentile',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmRevenuePercentileResolver' },
    metadata: {
      description: 'Calibration NTM revenue multiple percentile',
      category: 'valuation',
      summaryType: 'firm',
    },
  },

  [COLUMN_TYPES.CAL_REVENUE_MULTIPLE_TYPE]: {
    id: COLUMN_TYPES.CAL_REVENUE_MULTIPLE_TYPE,
    displayName: 'Calibration: Revenue Multiple Type',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calRevenueMultipleTypeResolver' },
    metadata: { description: 'Multiple type used for revenue calibration', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_LTM_REVENUE_PREMIUM]: {
    id: COLUMN_TYPES.CAL_LTM_REVENUE_PREMIUM,
    displayName: 'Calibration: LTM Revenue Premium',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmRevenuePremiumResolver' },
    metadata: { description: 'Calibration LTM revenue multiple premium', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_NTM_REVENUE_PREMIUM]: {
    id: COLUMN_TYPES.CAL_NTM_REVENUE_PREMIUM,
    displayName: 'Calibration: NTM Revenue Premium',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmRevenuePremiumResolver' },
    metadata: { description: 'Calibration NTM revenue multiple premium', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_LTM_EBITDA_MULTIPLE]: {
    id: COLUMN_TYPES.CAL_LTM_EBITDA_MULTIPLE,
    displayName: 'Calibration: LTM EBITDA Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmEbitdaMultipleResolver' },
    metadata: { description: 'Calibration LTM EBITDA multiple', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_NTM_EBITDA_MULTIPLE]: {
    id: COLUMN_TYPES.CAL_NTM_EBITDA_MULTIPLE,
    displayName: 'Calibration: NTM EBITDA Multiple',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmEbitdaMultipleResolver' },
    metadata: { description: 'Calibration NTM EBITDA multiple', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_LTM_EBITDA_PERCENTILE]: {
    id: COLUMN_TYPES.CAL_LTM_EBITDA_PERCENTILE,
    displayName: 'Calibration: LTM EBITDA Percentile',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmEbitdaPercentileResolver' },
    metadata: { description: 'Calibration LTM EBITDA multiple percentile', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_NTM_EBITDA_PERCENTILE]: {
    id: COLUMN_TYPES.CAL_NTM_EBITDA_PERCENTILE,
    displayName: 'Calibration: NTM EBITDA Percentile',
    displayFormat: DEFAULT_DISPLAY_FORMATS.percentage,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmEbitdaPercentileResolver' },
    metadata: { description: 'Calibration NTM EBITDA multiple percentile', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_EBITDA_MULTIPLE_TYPE]: {
    id: COLUMN_TYPES.CAL_EBITDA_MULTIPLE_TYPE,
    displayName: 'Calibration: EBITDA Multiple Type',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calEbitdaMultipleTypeResolver' },
    metadata: { description: 'Multiple type used for EBITDA calibration', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_LTM_EBITDA_PREMIUM]: {
    id: COLUMN_TYPES.CAL_LTM_EBITDA_PREMIUM,
    displayName: 'Calibration: LTM EBITDA Premium',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calLtmEbitdaPremiumResolver' },
    metadata: { description: 'Calibration LTM EBITDA multiple premium', category: 'valuation', summaryType: 'firm' },
  },

  [COLUMN_TYPES.CAL_NTM_EBITDA_PREMIUM]: {
    id: COLUMN_TYPES.CAL_NTM_EBITDA_PREMIUM,
    displayName: 'Calibration: NTM EBITDA Premium',
    displayFormat: DEFAULT_DISPLAY_FORMATS.multiple,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'calNtmEbitdaPremiumResolver' },
    metadata: { description: 'Calibration NTM EBITDA multiple premium', category: 'valuation', summaryType: 'firm' },
  },

  // ============================================
  // Documents & Process Management
  // ============================================
  [COLUMN_TYPES.TOTAL_DOCUMENTS]: {
    id: COLUMN_TYPES.TOTAL_DOCUMENTS,
    displayName: 'Total Documents',
    displayFormat: DEFAULT_DISPLAY_FORMATS.integer,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'totalDocumentsResolver' },
    metadata: { description: 'Total number of documents', category: 'process', summaryType: 'firm' },
  },

  [COLUMN_TYPES.REFERENCED_DOCUMENTS]: {
    id: COLUMN_TYPES.REFERENCED_DOCUMENTS,
    displayName: 'Referenced Documents',
    displayFormat: DEFAULT_DISPLAY_FORMATS.integer,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'referencedDocumentsResolver' },
    metadata: { description: 'Number of documents referenced', category: 'process', summaryType: 'firm' },
  },

  [COLUMN_TYPES.PENDING_DOCUMENT_REQUESTS]: {
    id: COLUMN_TYPES.PENDING_DOCUMENT_REQUESTS,
    displayName: 'Pending Document Requests',
    displayFormat: DEFAULT_DISPLAY_FORMATS.integer,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'pendingDocumentRequestsResolver' },
    metadata: { description: 'Number of pending document requests', category: 'process', summaryType: 'firm' },
  },

  [COLUMN_TYPES.PENDING_QUESTIONS]: {
    id: COLUMN_TYPES.PENDING_QUESTIONS,
    displayName: 'Pending Questions',
    displayFormat: DEFAULT_DISPLAY_FORMATS.integer,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'pendingQuestionsResolver' },
    metadata: { description: 'Number of pending questions', category: 'process', summaryType: 'firm' },
  },

  // ============================================
  // Process Management (optional)
  // ============================================
  [COLUMN_TYPES.PROCESS_STATUS]: {
    id: COLUMN_TYPES.PROCESS_STATUS,
    displayName: 'Status',
    displayFormat: DEFAULT_DISPLAY_FORMATS.text,
    kind: 'simple',
    dependsOn: [],
    dataSource: { type: 'custom', resolver: 'processStatusResolver' },
    metadata: {
      description: 'Process management status',
      category: 'custom',
      summaryType: 'fund',
    },
  },
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get a value type by ID
 */
export function getValueType(id: string): ValueType | undefined {
  return VALUE_TYPE_DEFINITIONS[id];
}

/**
 * Get all value types
 */
export function getAllValueTypes(): ValueType[] {
  return Object.values(VALUE_TYPE_DEFINITIONS);
}

/**
 * Get value types by category
 */
export function getValueTypesByCategory(category: ValueTypeCategory): ValueType[] {
  return getAllValueTypes().filter(vt => vt.metadata?.category === category);
}

/**
 * Get value types for a specific summary type
 */
export function getValueTypesForSummary(summaryType: 'firm' | 'fund'): ValueType[] {
  return getAllValueTypes().filter(
    vt => vt.metadata?.summaryType === 'both' || vt.metadata?.summaryType === summaryType
  );
}

/**
 * Get required value types (cannot be removed from spreadsheet)
 */
export function getRequiredValueTypes(): ValueType[] {
  return getAllValueTypes().filter(vt => vt.metadata?.isRequired === true);
}

/**
 * Get draft-sensitive value types
 */
export function getDraftSensitiveValueTypes(): ValueType[] {
  return getAllValueTypes().filter(vt => vt.metadata?.isDraftSensitive === true);
}

/**
 * Build a dependency graph for value types
 * Returns a map of value type ID -> dependent value type IDs
 */
export function buildDependencyGraph(): Map<string, string[]> {
  const graph = new Map<string, string[]>();
  const allValueTypes = getAllValueTypes();
  allValueTypes.forEach(vt => {
    // Find all value types that depend on this one
    const dependents = allValueTypes.filter(other => other.dependsOn.includes(vt.id)).map(other => other.id);
    graph.set(vt.id, dependents);
  });
  return graph;
}

/**
 * Get all value types that would be affected by removing a column
 * (includes transitive dependencies)
 */
export function getDependentValueTypes(valueTypeId: string): string[] {
  const graph = buildDependencyGraph();
  const result = new Set<string>();

  const traverse = (id: string) => {
    const dependents = graph.get(id) || [];
    dependents.forEach(dep => {
      if (!result.has(dep)) {
        result.add(dep);
        traverse(dep);
      }
    });
  };

  traverse(valueTypeId);
  return Array.from(result);
}

// ============================================
// Default Column Configurations
// ============================================

/**
 * Default columns for firm summary (in order)
 */
export const DEFAULT_FIRM_SUMMARY_COLUMNS: string[] = [
  COLUMN_TYPES.COMPANY_NAME,
  COLUMN_TYPES.FUND_NAME,
  COLUMN_TYPES.VALUATION_DATE,
  COLUMN_TYPES.INITIAL_INVESTMENT_DATE,
  COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE,
  COLUMN_TYPES.DILUTED_OWNERSHIP,
  COLUMN_TYPES.INVESTED_CAPITAL,
  COLUMN_TYPES.REALIZED_VALUE,
  COLUMN_TYPES.UNREALIZED_VALUE,
  COLUMN_TYPES.TOTAL_VALUE,
  COLUMN_TYPES.GROSS_IRR,
  COLUMN_TYPES.MOIC,
];

/**
 * Default columns for fund summary (in order)
 */
export const DEFAULT_FUND_SUMMARY_COLUMNS: string[] = [
  COLUMN_TYPES.COMPANY_NAME,
  COLUMN_TYPES.VALUATION_DATE,
  COLUMN_TYPES.INITIAL_INVESTMENT_DATE,
  COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE,
  COLUMN_TYPES.DILUTED_OWNERSHIP,
  COLUMN_TYPES.INVESTED_CAPITAL,
  COLUMN_TYPES.REALIZED_VALUE,
  COLUMN_TYPES.UNREALIZED_VALUE,
  COLUMN_TYPES.TOTAL_VALUE,
  COLUMN_TYPES.GROSS_IRR,
  COLUMN_TYPES.MOIC,
];

/**
 * Default columns for the Firm Valuations page (in order).
 * Deliberately valuation-focused and distinct from the firm summary default; no fund_name,
 * so the page shows a single row per portfolio company unless the Fund column is added back.
 */
export const DEFAULT_VALUATIONS_SUMMARY_COLUMNS: string[] = [
  COLUMN_TYPES.COMPANY_NAME,
  COLUMN_TYPES.VALUATION_DATE,
  COLUMN_TYPES.PROCESS_MANAGEMENT,
  COLUMN_TYPES.VALUATION_STATUS,
  COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD,
  COLUMN_TYPES.ENTERPRISE_VALUE,
  COLUMN_TYPES.EQUITY_VALUE,
  COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE,
  COLUMN_TYPES.ALLOCATION_METHOD,
  COLUMN_TYPES.CURRENT_FUND_VALUE,
  COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS,
];

/**
 * Resolve the default column set for a given summary page type.
 */
export const getDefaultColumnsForSummaryType = (summaryType: 'firm' | 'fund' | 'valuations'): string[] => {
  if (summaryType === 'fund') {
    return DEFAULT_FUND_SUMMARY_COLUMNS;
  }
  if (summaryType === 'valuations') {
    return DEFAULT_VALUATIONS_SUMMARY_COLUMNS;
  }
  return DEFAULT_FIRM_SUMMARY_COLUMNS;
};
